import type { BotConfig } from "./config";
import type { Filter, SailfishCallbacks, Trade, TradeRaw, PoolInit, TokenInit, TokenMint } from "sailfish-sdk";
import { botConfigs } from "../bots";

export const MAX_RETAINED_ITEMS = 50;

export type PoolStat = {
  pool_type: string;
  pool_address: string;
  token_0_mint: string;
  token_1_mint: string;
  tradesRawCount: number;
  wallets: Record<string, boolean>;
};

export type WalletStat = {
  address: string;
  quoteVolume: number;
  tradeCount: number;
  tokens: Set<string>;
};

export type LargestTrade = {
  amount: number;
  pool: string;
  tx: string;
};

export type Bot = {
  config: BotConfig;
  sailfish: Sailfish;
  updated_at: Date;
  trades: Trade[];
  trades_raw: TradeRaw[];
  poolInits: PoolInit[];
  tokenGraduates: PoolInit[];
  tokenInits: TokenInit[];
  tokenMints: TokenMint[];
  poolsStats: Record<string, PoolStat>;
  walletStats: Record<string, WalletStat>;
  totalBribes: number;
  totalFees: number;
  tradesWithBribes: number;
  totalTradeCount: number;
  largestTrade: LargestTrade | null;
};

export let STORE = $state({
  bot: {
    sailfish: null as any,
    config: {} as BotConfig,
    trades: [],
    trades_raw: [],
    poolInits: [],
    tokenInits: [],
    tokenMints: [],
    tokenGraduates: [],
    poolsStats: {} as Record<string, PoolStat>,
    walletStats: {} as Record<string, WalletStat>,
    totalBribes: 0,
    totalFees: 0,
    tradesWithBribes: 0,
    totalTradeCount: 0,
    largestTrade: null,
    updated_at: new Date(),
  } as Bot,
  poolInfoQueue: new Set<string>(),
});

const MAX_POOLS = 10_000;
const MAX_WALLETS = 10_000;

export function updateStats(tradeRaw: TradeRaw) {
  if (!STORE.bot.poolsStats[tradeRaw.pool_address]) {
    if (Object.keys(STORE.bot.poolsStats).length >= MAX_POOLS) return;
    STORE.bot.poolsStats[tradeRaw.pool_address] = {
      pool_type: tradeRaw.pool_type,
      pool_address: tradeRaw.pool_address,
      token_0_mint: tradeRaw.token_address_in,
      token_1_mint: tradeRaw.token_address_out,
      tradesRawCount: 0,
      wallets: {},
    };
  }
  STORE.bot.poolsStats[tradeRaw.pool_address].tradesRawCount += 1;
  STORE.bot.poolsStats[tradeRaw.pool_address].wallets[tradeRaw.from_wallet] = true;
  STORE.bot.poolsStats[tradeRaw.pool_address].wallets[tradeRaw.to_wallet] = true;
}

const callbacks: SailfishCallbacks = {
  onMessage: () => { },
  onTokenInit: (tokenInit: TokenInit) => {
    if (STORE.bot.tokenInits.length >= MAX_RETAINED_ITEMS) {
      STORE.bot.tokenInits.pop();
    }
    STORE.bot.tokenInits.unshift(tokenInit);
  },
  onTokenMint: (tokenMint: TokenMint) => {
    if (STORE.bot.tokenMints.length >= MAX_RETAINED_ITEMS) {
      STORE.bot.tokenMints.pop();
    }
    STORE.bot.tokenMints.unshift(tokenMint);
  },
  onTokenGraduate: (poolInit: PoolInit) => {
    if (STORE.bot.tokenGraduates.length >= MAX_RETAINED_ITEMS) {
      STORE.bot.tokenGraduates.pop();
    }
    STORE.bot.tokenGraduates.unshift(poolInit);
  },
  onPoolInit: (poolInit: PoolInit) => {
    if (STORE.bot.poolInits.length >= MAX_RETAINED_ITEMS) {
      STORE.bot.poolInits.pop();
    }
    STORE.bot.poolInits.unshift(poolInit);
  },
  onTradeRaw: (tradeRaw: TradeRaw) => {
    if (STORE.bot.trades_raw.length >= MAX_RETAINED_ITEMS) {
      STORE.bot.trades_raw.pop();
    }
    STORE.bot.trades_raw.unshift(tradeRaw);
    if (STORE.bot.sailfish.getPoolInfo(tradeRaw.pool_address) === null) {
      STORE.poolInfoQueue.add(tradeRaw.pool_address);
    }
    updateStats(tradeRaw);
    // Track bribes and fees
    const bribe = parseFloat(tradeRaw.bribe) || 0;
    const fee = parseFloat(tradeRaw.fee) || 0;
    STORE.bot.totalBribes += bribe;
    STORE.bot.totalFees += fee;
    STORE.bot.totalTradeCount += 1;
    if (bribe > 0) {
      STORE.bot.tradesWithBribes += 1;
    }
  },
  onTrade: (trade: Trade) => {
    if (STORE.bot.trades.length >= MAX_RETAINED_ITEMS) {
      STORE.bot.trades.pop();
    }
    STORE.bot.trades.unshift(trade);
    // Track wallet volume
    const wallet = trade.from_wallet;
    const quoteAmount = parseFloat(trade.quote_amount) || 0;
    if (!STORE.bot.walletStats[wallet]) {
      if (Object.keys(STORE.bot.walletStats).length >= MAX_WALLETS) {
        // Skip new wallets when at cap
      } else {
        STORE.bot.walletStats[wallet] = { address: wallet, quoteVolume: 0, tradeCount: 0, tokens: new Set() };
      }
    }
    if (!STORE.bot.walletStats[wallet]) return;
    STORE.bot.walletStats[wallet].quoteVolume += Math.abs(quoteAmount);
    STORE.bot.walletStats[wallet].tradeCount += 1;
    STORE.bot.walletStats[wallet].tokens.add(trade.base_token_address);
    // Track largest trade
    if (!STORE.bot.largestTrade || Math.abs(quoteAmount) > STORE.bot.largestTrade.amount) {
      STORE.bot.largestTrade = {
        amount: Math.abs(quoteAmount),
        pool: trade.pool_address,
        tx: trade.index.tx_hash,
      };
    }
  },
}

export async function initBot(botConfig: BotConfig, filter: Filter): Promise<Bot> {
  const { Sailfish, SailfishTier } = await import("sailfish-sdk");
  return {
    config: botConfig,
    sailfish: new Sailfish({
      callbacks,
      filter,
      tier: SailfishTier.demo({ apiKey: botConfig.sailfish_api }),
    }),
    trades: [],
    trades_raw: [],
    poolInits: [],
    tokenInits: [],
    tokenMints: [],
    tokenGraduates: [],
    updated_at: new Date(),
    poolsStats: {} as Record<string, PoolStat>,
    walletStats: {} as Record<string, WalletStat>,
    totalBribes: 0,
    totalFees: 0,
    tradesWithBribes: 0,
    totalTradeCount: 0,
    largestTrade: null,
  };
}

export function getBotConfig(botName: string): BotConfig {
  let config = botConfigs.find((botConfig) => botConfig.name === botName);
  if (!config) {
    alert(`Cannot get Bot Config for Bot ${botName} not found`);
    throw new Error(`Cannot get Bot Config for Bot ${botName} not found`);
  }
  return config;
}

export function getDexUrl(botName: string, pair_address: string): string {
  let botConfig = getBotConfig(botName);
  return `${botConfig.dexscreener}/${pair_address}?maker=${botConfig.wallet}`;
}

export function getExplorerUrl(botName: string): string {
  let botConfig = getBotConfig(botName);
  return botConfig.explorer;
}

export async function initStore() {
  if (STORE.bot.sailfish) return; // already initialized
  let botConfig = botConfigs[0];
  STORE.bot = await initBot(botConfig, {} as Filter);
  setInterval(async () => {
    let queueSize = STORE.poolInfoQueue.size;
    if (queueSize > 0) {
      const poolAddress = STORE.poolInfoQueue.values().next().value;
      if (poolAddress && STORE.bot.sailfish.getPoolInfo(poolAddress) === null) {
        try {
          await STORE.bot.sailfish.fetchPoolInfo(poolAddress);
        } catch {
          // silently ignore pool info fetch errors
        }
        STORE.poolInfoQueue.delete(poolAddress);
      }
    }
  }, 500);
}

